import React, { useEffect, useMemo, useState } from "react";
import { Body } from "@/styles/typography";
import { Button, Col, Row } from "@/styles/layout";
import { Div } from "@/styles/layout";
import { Variant, motion } from "framer-motion";
import { IconType } from "react-icons";
import { AiOutlineLoading } from "react-icons/ai";

interface ButtonProps {
  label: string;
  labelStyle?: any | undefined;
  containerStyle?: any | undefined;
  onClick?: (...i: any[]) => Promise<void> | void | undefined;
  disabled?: boolean;
  type?: "reset" | "button" | "submit";
  variant: "primary" | "outline" | "custom" | "outline_secondary" | "secondary";
  Icon?: IconType | undefined;
  iconSize?: number;
  isLoading?: boolean;
}

const variants = {
  primary: {
    active: {
      rest: {
        bg: "primary.accent",
        color: "primary.bgBase",
        borderColor: "primary.accent",
        borderWidth: 0,
      },
      hover: {
        bg: "primary.accentHover",
        color: "primary.bgBase",
        borderColor: "primary.border",
        borderWidth: 0,
      },
      focus: {
        bg: "primary.accent",
        color: "primary.bgBase",
        borderColor: "primary.borderActive",
        borderWidth: 0,
      },
    },
    disabled: {
      bg: "primary.elementActive",
      color: "primary.borderHover",
      borderColor: "primary.borderSubtle",
      borderWidth: 0,
    },
  },
  secondary: {
    active: {
      rest: {
        bg: "secondary.accent",
        color: "secondary.textHigh",
        borderColor: "secondary.accent",
        borderWidth: 0,
      },
      hover: {
        bg: "secondary.accentHover",
        color: "secondary.textHigh",
        borderColor: "secondary.border",
        borderWidth: 0,
      },
      focus: {
        bg: "secondary.accent",
        color: "secondary.textHigh",
        borderColor: "secondary.borderActive",
        borderWidth: 0,
      },
    },
    disabled: {
      bg: "secondary.elementActive",
      color: "secondary.textHigh",
      borderColor: "secondary.borderSubtle",
      borderWidth: 0,
    },
  },
  outline: {
    active: {
      rest: {
        bg: "transparent",
        color: "primary.textLow",
        borderColor: "primary.borderSubtle",
        borderWidth: 0.5,
      },
      hover: {
        bg: "transparent",
        color: "primary.textLow",
        borderColor: "primary.border",
        borderWidth: 0.5,
      },
      focus: {
        bg: "transparent",
        color: "primary.textLow",
        borderColor: "primary.borderActive",
        borderWidth: 0.5,
      },
    },
    disabled: {
      bg: "transparent",
      color: "primary.border",
      borderColor: "primary.border",
      borderWidth: 0.5,
    },
  },
  outline_secondary: {
    active: {
      rest: {
        bg: "transparent",
        color: "secondary.accent",
        borderColor: "secondary.accent",
        borderWidth: 0.5,
      },
      hover: {
        bg: "transparent",
        color: "secondary.accentHover",
        borderColor: "secondary.accentHover",
        borderWidth: 0.5,
      },
      focus: {
        bg: "transparent",
        color: "secondary.accentHover",
        borderColor: "secondary.accentHover",
        borderWidth: 0.5,
      },
    },
    disabled: {
      bg: "transparent",
      color: "secondary.border",
      borderColor: "secondary.border",
      borderWidth: 0.5,
    },
  },
};

const buttonVariants: Record<string, Variant> = {
  resting: {
    scale: 1,
  },
  hovered: {
    scale: 0.95,
  },
  pressed: {
    scale: 0.9,
    transition: {
      duration: 0.1,
    },
  },
  loading: {
    scale: 1.02,
    transition: {
      duration: 0.5,
      repeat: 100,
      repeatType: "reverse",
    },
  },
};

const MyButton = ({
  label,
  type = "button",
  variant = "primary",
  labelStyle,
  containerStyle,
  onClick,
  disabled = false,
  Icon,
  iconSize = 18,
  isLoading,
  ...props
}: ButtonProps) => {
  const [loading, setLoading] = useState(false);
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);

  const handleClick = async (...args: any[]) => {
    setLoading(true);
    try {
      // await sleep(10);
      await onClick?.(...args);
      setLoading(false);
    } catch (e) {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isLoading !== undefined) {
      setLoading(isLoading);
    }
  }, [isLoading]);

  const { bg, color, borderColor, borderWidth } = useMemo(() => {
    if (variant !== "custom") {
      if (disabled) {
        return variants[variant].disabled;
      } else if (focus) {
        return variants[variant].active.focus;
      } else if (hover) {
        return variants[variant].active.hover;
      }
      return variants[variant].active.rest;
    }
    return {
      bg: undefined,
      color: undefined,
      borderColor: undefined,
      borderWidth: undefined,
    };
  }, [hover, focus, disabled, variant]);

  return (
    <motion.button
      layout
      style={{
        cursor: "pointer",
        outline: "none",
        border: "none ",
        backgroundColor: "transparent",
        padding: 0,
      }}
      variants={buttonVariants}
      animate={"resting"}
      whileHover={!disabled ? "hovered" : undefined}
      whileTap="pressed"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setFocus(true)}
      onBlur={() => setFocus(false)}
    >
      <Div
        onClick={loading || disabled ? undefined : handleClick}
        display="flex"
        alignItems="center"
        justifyContent="center"
        fontWeight="600"
        disabled={disabled}
        height={44}
        borderRadius="4px"
        {...containerStyle}
        {...props}
        bg={bg}
        color={color}
        px="32px"
        type={type}
        animate={{ background: bg, borderColor, color }}
        borderWidth={borderWidth}
        borderStyle="solid"
        style={{ cursor: disabled ? "not-allowed" : "pointer" }}
      >
        <Row alignItems="center">
          <Col color={color} alignItems={"center"} justifyContent={"center"}>
            {loading && (
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ ease: "linear", duration: 0.5, repeat: Infinity }}
              >
                <AiOutlineLoading size={iconSize} />
              </motion.div>
            )}
            {!!Icon && !loading && <Icon size={iconSize} />}
          </Col>
          <Body
            ml={!!Icon || loading ? "16px" : "0"}
            fontSize={18}
            {...labelStyle}
            color={color}
          >
            {label}
          </Body>
        </Row>
      </Div>
    </motion.button>
  );
};

export default MyButton;
